/**
 * Persistence boundary for employee clock-in / clock-out actions.
 *
 * A punch is only considered successful after the row returned by Supabase (or
 * a narrowly-scoped read-back after an ambiguous response) matches the exact,
 * fixed payload supplied by the caller.
 */

export const ATTENDANCE_PUNCH_SELECT =
  'id, employee_code, work_date, clock_in_at, clock_out_at, break_minutes, duty_type, status';

export interface AttendancePunchInput {
  recordId?: string | null;
  employeeCode: string;
  workDate: string;
  clockInAt: string;
  clockOutAt: string | null;
  breakMinutes: number;
  dutyType: string;
  status: string;
}

export interface PersistedAttendancePunch {
  id: string;
  employee_code: string;
  work_date: string;
  clock_in_at: string;
  clock_out_at: string | null;
  break_minutes: number;
  duty_type: string;
  status: string;
}

export type AttendancePunchPersistenceErrorCode =
  | 'invalid_input'
  | 'write_failed'
  | 'verification_failed'
  | 'conflict';

export class AttendancePunchPersistenceError extends Error {
  readonly code: AttendancePunchPersistenceErrorCode;
  readonly causeValue?: unknown;

  constructor(
    code: AttendancePunchPersistenceErrorCode,
    message: string,
    causeValue?: unknown
  ) {
    super(message);
    Object.setPrototypeOf(this, AttendancePunchPersistenceError.prototype);
    this.name = 'AttendancePunchPersistenceError';
    this.code = code;
    this.causeValue = causeValue;
  }
}

type PunchKind = 'in' | 'out';

interface QueryResponse {
  data: unknown;
  error: unknown;
}

interface ReadBackResult {
  row: PersistedAttendancePunch | null;
  error: unknown;
}

/**
 * Supabase's generated query-builder type is intentionally kept at this
 * boundary. This lets the UI pass the normal SupabaseClient while keeping the
 * persistence contract independently testable.
 */
type SupabaseLikeClient = {
  from: (table: string) => any;
};

const asClient = (client: unknown): SupabaseLikeClient => {
  if (!client || typeof (client as SupabaseLikeClient).from !== 'function') {
    throw new AttendancePunchPersistenceError(
      'invalid_input',
      'Supabaseクライアントが設定されていません。'
    );
  }
  return client as SupabaseLikeClient;
};

const isValidWorkDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

const validateInput = (kind: PunchKind, input: AttendancePunchInput): void => {
  if (
    !input.employeeCode ||
    !input.workDate ||
    !input.clockInAt ||
    !input.dutyType ||
    !input.status ||
    !Number.isFinite(input.breakMinutes) ||
    !Number.isInteger(input.breakMinutes) ||
    input.breakMinutes < 0 ||
    !Number.isFinite(Date.parse(input.clockInAt)) ||
    !isValidWorkDate(input.workDate)
  ) {
    throw new AttendancePunchPersistenceError(
      'invalid_input',
      '打刻データに必要な項目が不足しています。'
    );
  }

  if (kind === 'in' && input.clockOutAt !== null) {
    throw new AttendancePunchPersistenceError(
      'invalid_input',
      '出勤打刻の退勤時刻はnullである必要があります。'
    );
  }

  if (kind === 'out' && !input.clockOutAt) {
    throw new AttendancePunchPersistenceError(
      'invalid_input',
      '退勤打刻の時刻が指定されていません。'
    );
  }

  if (input.clockOutAt !== null && !Number.isFinite(Date.parse(input.clockOutAt))) {
    throw new AttendancePunchPersistenceError(
      'invalid_input',
      '退勤打刻の時刻形式が正しくありません。'
    );
  }
};

const toDatabasePayload = (input: AttendancePunchInput) => ({
  clock_in_at: input.clockInAt,
  clock_out_at: input.clockOutAt,
  break_minutes: input.breakMinutes,
  duty_type: input.dutyType,
  status: input.status
});

const toPunchOutUpdatePayload = (input: AttendancePunchInput) => ({
  clock_out_at: input.clockOutAt,
  break_minutes: input.breakMinutes,
  duty_type: input.dutyType,
  status: input.status
});

const toInsertPayload = (input: AttendancePunchInput) => ({
  ...toDatabasePayload(input),
  employee_code: input.employeeCode,
  work_date: input.workDate
});

const firstRow = (data: unknown): unknown => {
  if (Array.isArray(data)) {
    return data[0] ?? null;
  }
  return data ?? null;
};

const normalizeRow = (value: unknown): PersistedAttendancePunch | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const row = value as Record<string, unknown>;
  const breakMinutes = Number(row.break_minutes);
  if (
    row.id === null ||
    row.id === undefined ||
    typeof row.employee_code !== 'string' ||
    typeof row.work_date !== 'string' ||
    typeof row.clock_in_at !== 'string' ||
    (row.clock_out_at !== null && typeof row.clock_out_at !== 'string') ||
    !Number.isFinite(breakMinutes) ||
    typeof row.duty_type !== 'string' ||
    typeof row.status !== 'string'
  ) {
    return null;
  }

  return {
    id: String(row.id),
    employee_code: row.employee_code,
    work_date: row.work_date,
    clock_in_at: row.clock_in_at,
    clock_out_at: row.clock_out_at as string | null,
    break_minutes: breakMinutes,
    duty_type: row.duty_type,
    status: row.status
  };
};

const timestampsMatch = (left: string | null, right: string | null): boolean => {
  if (left === null || right === null) {
    return left === right;
  }
  if (left === right) {
    return true;
  }
  const leftEpoch = Date.parse(left);
  const rightEpoch = Date.parse(right);
  return (
    Number.isFinite(leftEpoch) &&
    Number.isFinite(rightEpoch) &&
    leftEpoch === rightEpoch
  );
};

export const attendancePunchMatches = (
  row: PersistedAttendancePunch,
  input: AttendancePunchInput
): boolean =>
  row.employee_code === input.employeeCode &&
  row.work_date === input.workDate &&
  timestampsMatch(row.clock_in_at, input.clockInAt) &&
  timestampsMatch(row.clock_out_at, input.clockOutAt) &&
  row.break_minutes === input.breakMinutes &&
  row.duty_type === input.dutyType &&
  row.status === input.status;

const assertMatchingRow = (
  rowValue: unknown,
  input: AttendancePunchInput
): PersistedAttendancePunch => {
  const row = normalizeRow(rowValue);
  if (!row) {
    throw new AttendancePunchPersistenceError(
      'verification_failed',
      '保存結果の確認に必要なデータが返されませんでした。'
    );
  }

  if (!attendancePunchMatches(row, input)) {
    if (
      input.clockOutAt !== null &&
      row.clock_out_at !== null &&
      !timestampsMatch(row.clock_out_at, input.clockOutAt)
    ) {
      throw conflictError(row, input);
    }
    throw new AttendancePunchPersistenceError(
      'verification_failed',
      '保存された打刻内容が送信内容と一致しません。'
    );
  }

  return row;
};

const conflictError = (
  row: PersistedAttendancePunch,
  input: AttendancePunchInput
): AttendancePunchPersistenceError =>
  new AttendancePunchPersistenceError(
    'conflict',
    `この勤務は既に別の退勤時刻（${row.clock_out_at}）で保存されています。送信時刻: ${input.clockOutAt}`
  );

const readById = async (
  client: SupabaseLikeClient,
  recordId: string
): Promise<ReadBackResult> => {
  const response = (await client
    .from('attendance_records')
    .select(ATTENDANCE_PUNCH_SELECT)
    .eq('id', recordId)
    .limit(1)) as QueryResponse;

  return {
    row: normalizeRow(firstRow(response.data)),
    error: response.error
  };
};

const readByNaturalKey = async (
  client: SupabaseLikeClient,
  input: AttendancePunchInput
): Promise<ReadBackResult> => {
  const response = (await client
    .from('attendance_records')
    .select(ATTENDANCE_PUNCH_SELECT)
    .eq('employee_code', input.employeeCode)
    .eq('work_date', input.workDate)
    .limit(1)) as QueryResponse;

  return {
    row: normalizeRow(firstRow(response.data)),
    error: response.error
  };
};

const readTarget = async (
  client: SupabaseLikeClient,
  input: AttendancePunchInput
): Promise<ReadBackResult> => {
  if (input.recordId) {
    const byId = await readById(client, input.recordId);
    if (byId.error || byId.row) {
      return byId;
    }
  }
  return readByNaturalKey(client, input);
};

const verifyReadBack = (
  result: ReadBackResult,
  input: AttendancePunchInput
): PersistedAttendancePunch | null => {
  if (result.error) {
    return null;
  }
  if (!result.row) {
    return null;
  }
  if (attendancePunchMatches(result.row, input)) {
    return result.row;
  }
  if (
    input.clockOutAt !== null &&
    result.row.clock_out_at !== null &&
    !timestampsMatch(result.row.clock_out_at, input.clockOutAt)
  ) {
    throw conflictError(result.row, input);
  }
  return null;
};

const updateExisting = async (
  client: SupabaseLikeClient,
  kind: PunchKind,
  recordId: string,
  input: AttendancePunchInput
): Promise<PersistedAttendancePunch | null> => {
  let query = client
    .from('attendance_records')
    .update(kind === 'out' ? toPunchOutUpdatePayload(input) : toDatabasePayload(input))
    .eq('id', recordId)
    .eq('employee_code', input.employeeCode)
    .eq('work_date', input.workDate);

  // Prevent a delayed/retried request from overwriting a clock-out that another
  // device has already committed.
  if (kind === 'out') {
    query = query
      .eq('clock_in_at', input.clockInAt)
      .is('clock_out_at', null);
  }

  const response = (await query
    .select(ATTENDANCE_PUNCH_SELECT)
    .limit(1)) as QueryResponse;

  if (response.error) {
    throw response.error;
  }

  const returned = firstRow(response.data);
  if (returned) {
    return assertMatchingRow(returned, input);
  }

  // A zero-row update is never followed by a blind insert. First determine
  // whether the requested state already exists, conflicts, or the row is truly
  // absent.
  const readBack = await readTarget(client, { ...input, recordId });
  if (readBack.error) {
    throw readBack.error;
  }
  if (readBack.row) {
    if (attendancePunchMatches(readBack.row, input)) {
      return readBack.row;
    }
    if (
      kind === 'out' &&
      readBack.row.clock_out_at !== null &&
      !timestampsMatch(readBack.row.clock_out_at, input.clockOutAt)
    ) {
      throw conflictError(readBack.row, input);
    }
    throw new AttendancePunchPersistenceError(
      'verification_failed',
      '対象の勤務レコードは存在しますが、更新できませんでした。'
    );
  }

  return null;
};

const insertNew = async (
  client: SupabaseLikeClient,
  input: AttendancePunchInput
): Promise<PersistedAttendancePunch> => {
  const response = (await client
    .from('attendance_records')
    .insert(toInsertPayload(input))
    .select(ATTENDANCE_PUNCH_SELECT)
    .limit(1)) as QueryResponse;

  if (response.error) {
    throw response.error;
  }

  const returned = firstRow(response.data);
  if (returned) {
    return assertMatchingRow(returned, input);
  }

  const readBack = await readByNaturalKey(client, input);
  const verified = verifyReadBack(readBack, input);
  if (verified) {
    return verified;
  }
  if (readBack.error) {
    throw readBack.error;
  }
  throw new AttendancePunchPersistenceError(
    'verification_failed',
    '打刻の追加後に保存結果を確認できませんでした。'
  );
};

const persistOnce = async (
  client: SupabaseLikeClient,
  kind: PunchKind,
  input: AttendancePunchInput
): Promise<PersistedAttendancePunch> => {
  let targetId = input.recordId ?? null;

  if (!targetId) {
    const existing = await readByNaturalKey(client, input);
    if (existing.error) {
      throw existing.error;
    }
    if (existing.row) {
      if (attendancePunchMatches(existing.row, input)) {
        return existing.row;
      }
      if (
        kind === 'out' &&
        existing.row.clock_out_at !== null &&
        !timestampsMatch(existing.row.clock_out_at, input.clockOutAt)
      ) {
        throw conflictError(existing.row, input);
      }
      targetId = existing.row.id;
    }
  }

  if (targetId) {
    const updated = await updateExisting(client, kind, targetId, input);
    if (updated) {
      return updated;
    }
  }

  // updateExisting/readByNaturalKey proved that no target exists. The unique
  // (employee_code, work_date) constraint remains the final concurrency guard.
  return insertNew(client, input);
};

const errorDetails = (error: unknown): { message: string; code: string; status: number | null } => {
  if (!error || typeof error !== 'object') {
    return { message: String(error), code: '', status: null };
  }
  const value = error as Record<string, unknown>;
  const parsedStatus = Number(value.status ?? value.statusCode ?? NaN);
  return {
    message: typeof value.message === 'string' ? value.message : String(error),
    code: typeof value.code === 'string' ? value.code : '',
    status: Number.isFinite(parsedStatus) ? parsedStatus : null
  };
};

const isTransientError = (error: unknown): boolean => {
  if (error instanceof AttendancePunchPersistenceError) {
    return false;
  }
  const { message, code, status } = errorDetails(error);
  if (status !== null && (status === 408 || status === 425 || status === 429 || status >= 500)) {
    return true;
  }
  if (['ETIMEDOUT', 'ECONNRESET', 'ECONNREFUSED', 'EAI_AGAIN', 'PGRST000', 'PGRST001'].includes(code)) {
    return true;
  }
  return /failed to fetch|load failed|network|timeout|timed out|connection|temporar|gateway|response.+lost|aborted/i.test(
    message
  );
};

const persistWithVerification = async (
  rawClient: unknown,
  kind: PunchKind,
  input: AttendancePunchInput
): Promise<PersistedAttendancePunch> => {
  const client = asClient(rawClient);
  validateInput(kind, input);

  // The input object is copied once. Both attempts and every verification read
  // use the same timestamps/status/break data even if the UI clock advances.
  const fixedInput: AttendancePunchInput = { ...input };

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      return await persistOnce(client, kind, fixedInput);
    } catch (error) {
      if (
        error instanceof AttendancePunchPersistenceError &&
        (error.code === 'conflict' || error.code === 'invalid_input')
      ) {
        throw error;
      }

      // A timeout/response loss may happen after the database committed. Read
      // the one target row before deciding whether a retry is necessary.
      let readBack: ReadBackResult;
      try {
        readBack = await readTarget(client, fixedInput);
      } catch (readError) {
        readBack = { row: null, error: readError };
      }

      const verified = verifyReadBack(readBack, fixedInput);
      if (verified) {
        return verified;
      }

      const retryable = isTransientError(error) || isTransientError(readBack.error);
      if (attempt === 0 && retryable) {
        continue;
      }

      const details = errorDetails(error);
      throw new AttendancePunchPersistenceError(
        error instanceof AttendancePunchPersistenceError
          ? error.code
          : 'write_failed',
        `打刻データを保存できませんでした: ${details.message}`,
        error
      );
    }
  }

  // The loop always returns or throws; this keeps the type contract explicit.
  throw new AttendancePunchPersistenceError(
    'write_failed',
    '打刻データを保存できませんでした。'
  );
};

export const persistPunchIn = (
  client: unknown,
  input: AttendancePunchInput
): Promise<PersistedAttendancePunch> => persistWithVerification(client, 'in', input);

export const persistPunchOut = (
  client: unknown,
  input: AttendancePunchInput
): Promise<PersistedAttendancePunch> => persistWithVerification(client, 'out', input);
